import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import DataTable, { CellPrimary, CellSecondary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import {
  useEventYearFilter,
  withEventYearFilterContext,
} from 'components/Events/Components/EventYearFilter';
import { useGetUserEvents } from 'components/Events/gql';
import YearSelect from 'components/FormFields/YearSelect';
import PageContainer from 'components/PageContainer';
import { HeaderContainer, HeaderTitle } from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import SearchInput from 'components/SearchInput';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import PermissionModal from 'pages/track/PermissionModal/PermissionModal';
import { AppContext } from 'shared/AppContext';
import { formatEventDate } from 'shared/formatters';
import GuestFormModal from './components/GuestFormModal';

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) => {
    switch (props.status?.toLowerCase()) {
      case 'published':
        return props.theme.name === 'dark'
          ? 'rgba(34, 197, 94, 0.2)'
          : 'rgba(34, 197, 94, 0.1)';
      case 'draft':
        return props.theme.name === 'dark'
          ? 'rgba(234, 179, 8, 0.2)'
          : 'rgba(234, 179, 8, 0.1)';
      case 'cancelled':
        return props.theme.name === 'dark'
          ? 'rgba(239, 68, 68, 0.2)'
          : 'rgba(239, 68, 68, 0.1)';
      default:
        return props.theme.name === 'dark'
          ? 'rgba(148, 163, 184, 0.2)'
          : 'rgba(148, 163, 184, 0.1)';
    }
  }};
  color: ${(props) => {
    switch (props.status?.toLowerCase()) {
      case 'published':
        return '#22C55E';
      case 'draft':
        return '#EAB308';
      case 'cancelled':
        return '#EF4444';
      default:
        return props.theme.name === 'dark' ? '#94A3B8' : '#64748B';
    }
  }};
`;

const TrackGuestLists = () => {
  const [events, setEvents] = useState([]);
  const [count, setCount] = useState(0);
  const [showAddGuestModal, setShowAddGuestModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  const { pathname } = useLocation();
  const isTrackAdmin = pathname.includes('/admin-track');
  const isEmployeeAdmin = pathname.includes('/admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const {
    state: { capabilities },
  } = useContext(AppContext);

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Year filter from context and URL
  const { yearFilter, setYearFilter } = useEventYearFilter();
  const thisYear = new Date().getFullYear();

  // Get year from URL or use context value
  const urlYear = input.year ? Number(input.year) : null;
  const currentYear = urlYear || yearFilter || thisYear;

  // Sync URL year with context on mount
  useEffect(() => {
    if (urlYear && urlYear !== yearFilter) {
      setYearFilter(urlYear);
    } else if (!urlYear && yearFilter) {
      // If no URL year but context has year, update URL
      updateQuery({ year: yearFilter });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleYearChange = (e) => {
    const newYear = Number(e.target.value);
    setYearFilter(newYear);
    updateQuery({ year: newYear, page: 1 });
  };

  // Fetch events data
  const { data, loading } = useGetUserEvents();

  useEffect(() => {
    if (data?.getEventsByAccount) {
      setEvents(data.getEventsByAccount.results || []);
      setCount(data.getEventsByAccount.count || 0);
    }
  }, [data]);

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle add guest button
  const handleAddGuest = () => {
    if (!capabilities || capabilities?.add_guests) {
      setShowAddGuestModal(true);
    } else {
      setShowPermissionModal(true);
    }
  };

  // Handle guest created
  const handleGuestCreated = () => {
    // Could add SMS notification modal here if needed
  };

  // DataTable columns configuration
  const columns = [
    {
      field: 'name',
      header: 'Event',
      mobileTitle: true,
      render: (event) => <CellPrimary>{event.name}</CellPrimary>,
    },
    {
      field: 'track',
      header: 'Track',
      render: (event) => (
        <CellSecondary>{event.track?.name || ''}</CellSecondary>
      ),
    },
    {
      field: 'date',
      header: 'Date',
      mobileSubtitle: true,
      render: (event) => (
        <CellSecondary>{formatEventDate(event)}</CellSecondary>
      ),
    },
    {
      field: 'nextGateTime',
      header: 'Gate Time',
      render: (event) => event.nextGateTime || '-',
    },
    {
      field: 'status',
      header: 'Status',
      render: (event) => (
        <StatusBadge status={event.status}>{event.status}</StatusBadge>
      ),
    },
  ];

  // DataTable actions configuration
  const actions = [
    {
      type: 'view',
      title: 'View Guest List',
      mobileLabel: 'View',
      to: (event) => `${routePrefix}/guest-lists/event/${event.id}`,
    },
  ];

  const totalPages = Math.ceil(count / 15);

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="users" />
            <HeaderTitle>Guest Lists</HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Content Card */}
        <Card noPadding>
          <ContentWrapper>
            {/* Page Header with Search */}
            <PageToolbar>
              <SearchInput
                onSearch={handleSearch}
                placeholder="Search events..."
                debounceMs={500}
              />
              <YearSelect value={currentYear} onChange={handleYearChange} />
              <ButtonGroup>
                <ButtonV2
                  onClick={handleAddGuest}
                  style={{ width: 'auto', flexShrink: 0 }}
                >
                  Add New Guest
                </ButtonV2>
                <ButtonV2
                  as={Link}
                  to={`${routePrefix}/guests`}
                  style={{
                    width: 'auto',
                    flexShrink: 0,
                    textDecoration: 'none',
                  }}
                >
                  View All Guests
                </ButtonV2>
              </ButtonGroup>
            </PageToolbar>

            {/* DataTable */}
            <DataTable
              columns={columns}
              data={events}
              loading={loading}
              keyField="id"
              emptyMessage="No events found"
              emptySubtext="Try adjusting your search or year filter"
              actions={actions}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </ContentWrapper>
        </Card>

        {/* Add Guest Modal */}
        <GuestFormModal
          isOpen={showAddGuestModal}
          onClose={() => setShowAddGuestModal(false)}
          onGuestCreated={handleGuestCreated}
        />

        {/* Permission Modal */}
        <PermissionModal
          isVisible={showPermissionModal}
          hideModal={() => setShowPermissionModal(false)}
          link={`${routePrefix}/settings/2`}
        />
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default withEventYearFilterContext(TrackGuestLists);
